/** The AI spend ledger (backend metaplan A5, AIE §9/§10) — `console_ai_spend_view`:
 *
 *  - `GET /ai_spend/` — one row per metered LLM call (tenant — `tenant_id` 0 and `tenant`
 *    null for internal spend —, user, feature, stage, model, tokens, cost, correlation id,
 *    background task), newest first or by cost, limit/offset.
 *  - `GET /ai_spend/summary/?group_by=tenant|user|feature|day|model` — totals plus one
 *    labelled row per group (cost first; days in date order), at most 500, and the list of
 *    metered features for the filter.
 *
 *  Both take the same filters. Shapes come from the generated spec types. */
import { apiGet, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

type Schemas = components['schemas'];

export type AISpendEntry = Schemas['AISpendEntry'];
export type AISpendSummary = Schemas['AISpendSummary'];
export type AISpendSummaryRow = Schemas['AISpendSummaryRow'];
export type AISpendMetrics = Schemas['AISpendMetrics'];
export type AISpendGroupBy = AISpendSummary['group_by'];

export const AI_SPEND_GROUPS: { value: AISpendGroupBy; label: string }[] = [
  { value: 'feature', label: 'Feature' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'user', label: 'User' },
  { value: 'day', label: 'Day' },
  { value: 'model', label: 'Model' },
];

/** The filters both endpoints take. `tour_operator: 0` is internal (no-tenant) spend. */
export interface AISpendFilters {
  /** One id or a comma list of ids (D1); `0` alone = internal (no-tenant) spend. */
  tour_operator?: TenantIdsParam;
  user?: number;
  feature?: string;
  correlation_id?: string;
  bgtask_id?: string;
  created_after?: string;
  created_before?: string;
  timeout?: 'extended';
}

export interface AISpendListParams extends AISpendFilters {
  limit?: number;
  offset?: number;
  ordering?: string;
}

function toQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: AISpendListParams = {}): Promise<PaginatedResponse<AISpendEntry>> {
  return apiGet(`/ai_spend/${toQuery({ ...params })}`, extendedListConfig(params.timeout));
}

export function summary(groupBy: AISpendGroupBy, filters: AISpendFilters = {}): Promise<AISpendSummary> {
  return apiGet(`/ai_spend/summary/${toQuery({ ...filters, group_by: groupBy })}`, extendedListConfig(filters.timeout));
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
const usdFine = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

/** Dollars: cents for totals, four decimals below a cent (a single triage call costs less). */
export function formatUsd(value: number): string {
  return value !== 0 && Math.abs(value) < 0.01 ? usdFine.format(value) : usd.format(value);
}
